import React, { useState } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { User, Clock, CheckCircle, XCircle } from 'lucide-react';
import { User as UserType } from '../types';

interface WorkConfirmationDialogProps {
  isOpen: boolean;
  user: UserType | null;
  action: 'start' | 'stop' | 'activate';
  onConfirm: () => void;
  onCancel: () => void;
}

const WorkConfirmationDialog: React.FC<WorkConfirmationDialogProps> = ({
  isOpen,
  user,
  action,
  onConfirm,
  onCancel
}) => {
  const [confirmationStep, setConfirmationStep] = useState<'initial' | 'final'>('initial');

  const handleFirstConfirmation = () => {
    setConfirmationStep('final');
  };

  const handleFinalConfirmation = () => {
    onConfirm();
    setConfirmationStep('initial');
  };

  const handleCancel = () => {
    onCancel();
    setConfirmationStep('initial');
  };

  const getActionText = (action: 'start' | 'stop' | 'activate') => {
    switch (action) {
      case 'start': return 'începi';
      case 'stop': return 'termini';
      case 'activate': return 'activezi pentru';
      default: return 'începi';
    }
  };
  
  const getActionIcon = (action: 'start' | 'stop' | 'activate') => {
    switch (action) {
      case 'start': return Clock;
      case 'stop': return CheckCircle;
      case 'activate': return User;
      default: return Clock;
    }
  };
  
  const actionText = getActionText(action);
  const ActionIcon = getActionIcon(action);

  return (
    <Dialog open={isOpen} onOpenChange={handleCancel}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-bold">
            {confirmationStep === 'initial' ? 'Confirmare Activitate' : 'Confirmare Finală'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="text-center py-6">
          <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
            <User className="w-8 h-8 text-blue-600" />
          </div>
          
          {confirmationStep === 'initial' ? (
            <>
              <h3 className="text-lg font-semibold mb-2">
                Ești sigur că ești?
              </h3>
              <p className="text-2xl font-bold text-blue-600 mb-4">
                {user?.name}
              </p>
              <p className="text-gray-600 mb-6">
                {action === 'activate' ? 
                  `Și vrei să ${actionText} lucrul?` : 
                  `Și vrei să ${actionText} lucrul?`
                }
              </p>
              
              <div className="flex gap-4 justify-center">
                <Button
                  onClick={handleFirstConfirmation}
                  className="bg-green-600 hover:bg-green-700 px-8 py-3"
                  size="lg"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Da
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  className="border-red-300 text-red-600 hover:bg-red-50 px-8 py-3"
                  size="lg"
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  Nu
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                <ActionIcon className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">
                Confirmă
              </h3>
              <p className="text-xl font-bold text-orange-600 mb-4">
                {user?.name}
              </p>
              <p className="text-gray-600 mb-6">
                {action === 'activate' ? 
                  `Confirmă că vrei să ${actionText} lucrul acum?` :
                  `Confirmă că vrei să ${actionText} lucrul acum?`
                }
              </p>
              
              <div className="flex gap-4 justify-center">
                <Button
                  onClick={handleFinalConfirmation}
                  className="bg-blue-600 hover:bg-blue-700 px-8 py-3"
                  size="lg"
                >
                  <CheckCircle className="w-5 h-5 mr-2" />
                  Confirmă
                </Button>
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  className="border-gray-300 text-gray-600 hover:bg-gray-50 px-8 py-3"
                  size="lg"
                >
                  <XCircle className="w-5 h-5 mr-2" />
                  Anulează
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WorkConfirmationDialog;